import { useContext, useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { isJwtExpired } from '../core/jwt';
import { RideStatus } from '../core/enums';
import { AuthContext } from '../context/AuthContextProvider';
import { AppStateContext } from '../context/AppStateContextProvider';

function DelayedNavigate({ to, delay = 1_000, replace = true }) {
    const [shouldNavigate, setShouldNavigate] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShouldNavigate(true);
        }, delay);

        return () => clearTimeout(timer);
    }, [delay]);

    return shouldNavigate ? <Navigate to={to} replace={replace} /> : null;
}

function renderNavigate(path, currentPath, pathsToIgnore, delay) {
    if (path === currentPath) {
        return null;
    }

    if (pathsToIgnore.includes(currentPath)) {
        return null;
    }

    return <DelayedNavigate to={path} delay={delay} />;
}

export default function AutoNavigate({ delay = 0 }) {
    const location = useLocation();
    const currentPath = location.pathname;

    const { tokens, loginOtpRequest, signUpRequest } = useContext(AuthContext);
    const { userCurrentRide, lastCompletedRideDetails } = useContext(AppStateContext);

    const isLoggedIn = tokens.refresh && !isJwtExpired(tokens.refresh);
    const shouldLoginOrSignUp =
        loginOtpRequest === null || loginOtpRequest.state === 'REQUEST_LOGIN_OTP';
    const shouldVerifyLoginOtp =
        signUpRequest === null &&
        loginOtpRequest !== null &&
        loginOtpRequest.state === 'VERIFY_LOGIN_OTP';
    const shouldVerifySignUpOtp =
        loginOtpRequest === null &&
        signUpRequest !== null &&
        signUpRequest.state === 'VERIFY_SIGNUP_OTP';

    if (!isLoggedIn) {
        if (shouldVerifyLoginOtp) {
            return renderNavigate(
                '/auth/verify-login-otp',
                currentPath,
                ['/auth/request-login-otp', '/auth/request-signup-otp'],
                delay
            );
        }

        if (shouldVerifySignUpOtp) {
            return renderNavigate(
                '/auth/verify-signup-otp',
                currentPath,
                ['/auth/request-signup-otp', '/auth/request-login-otp'],
                delay
            );
        }

        if (shouldLoginOrSignUp) {
            return renderNavigate(
                '/auth/request-login-otp',
                currentPath,
                ['/auth/request-signup-otp'],
                delay
            );
        }

        return renderNavigate(
            '/',
            currentPath,
            ['/auth/request-login-otp', '/auth/request-signup-otp'],
            delay
        );
    }

    if (
        (userCurrentRide === null && lastCompletedRideDetails !== null) ||
        (userCurrentRide !== null && userCurrentRide.status === RideStatus.COMPLETED)
    ) {
        return renderNavigate(
            '/protected/ride/completed',
            currentPath,
            ['/protected/profile'],
            delay
        );
    }

    if (userCurrentRide === null) {
        return renderNavigate('/protected/ride/idle', currentPath, ['/protected/profile'], delay);
    }

    if (userCurrentRide.status === RideStatus.BOOKING_INITIALIZED) {
        return renderNavigate(
            '/protected/ride/booking-initialized',
            currentPath,
            ['/protected/profile'],
            delay
        );
    }

    if (userCurrentRide.status === RideStatus.BOOKING_QUEUED) {
        return renderNavigate(
            '/protected/ride/booking-queued',
            currentPath,
            ['/protected/profile'],
            delay
        );
    }

    if (userCurrentRide.status === RideStatus.IN_PROGRESS) {
        return renderNavigate(
            '/protected/ride/in-ride',
            currentPath,
            ['/protected/profile'],
            delay
        );
    }

    return null;
}
